import { FormControl, FormGroup } from "@angular/forms";
import { AssessmentPdfGenerator } from "src/app/patients/dashboard-page/assessments-history-tab/assessment-to-pdf";
import { ControlsOf, AssessmentHelpers } from "./shared";
import { Patient } from "../patient.model";

interface GenitourinaryFields {
  /**
   * The color of the patient's urine.
   *
   * Options: yellow, dark yellow, or blood tinged.
   */
  urineColor: string;

  /**
   * The characteristics of the patient's urine.
   *
   * Options: cloudy, sediment, and odor.
   */
  urineCharacteristics: string[];

  /**
   * The pattern of urination the patient exhibits.
   *
   * Options: frequency, nocturia, dysuria, incontinence, retention, and inappropriate for developmental age (ped only).
   */
  urinaryPattern: string[];


  // Pediatric only
  /**
   * The abnormality of the patient's testes.
   */
  abnormalTestes: boolean;
  testesAbnormalityDetails: string;

  /**
   * The abnormality of the patient's vaginal opening.
   */
  abnormalVaginalOpening: boolean;
  vaginalOpeningAbnormalityDetails: string;


  foleyJustification: string;
  foleySecurement: string;

  // OB only
  hasAbnormalLochia: boolean;
  abnormalLochiaHasInappropriateColor: boolean;
  abnormalLochiaHasHeavyBleeding: boolean;
  abnormalLochiaHasFoulOdor: boolean;
  abnormalLochiaOther: string;

  hasAbnormalUterine: boolean;
  abnormalUterineFundalLocation: string;
  abnormalUterineFundalFeel: string;
  areContractionsPresent: boolean;
  contractionsStrength: string;
  contractionsDuration: string;
  contractionsFrequency: string;
  hasFetalHeartTones: boolean;
  fetalHeartTonesAccelerationsNoted: string;
  fetalHeartTonesDecelerationsNoted: string;
  fetalHeartTonesVariability: string;
  hasAbnormalVaginalAssessment: boolean;
  abnormalVaginalEpisiotomy: string;
  abnormalVaginalTear: string;
  abnormalVaginalDilation: string;
  abnormalVaginalStation: string;
  otherFindings: string;
}
export type GenitourinaryFormGroup = FormGroup<ControlsOf<GenitourinaryFields>>;

export const GenitourinaryAssessmentHelpers: AssessmentHelpers<GenitourinaryAssessment, Patient> = {
  createFormGroup: createGenitourinaryFormGroup,
  toPDF: genitourinaryAssessmentToPDF,
  formToObject: formToGenitourinaryAssessment,
  initialState: {
    urineColor: '',
    urineCharacteristics: [],
    urinaryPattern: [],
    abnormalTestes: false,
    testesAbnormalityDetails: '',
    abnormalVaginalOpening: false,
    vaginalOpeningAbnormalityDetails: '',
    foleyJustification: '',
    foleySecurement: '',
    hasAbnormalLochia: false,
    abnormalLochiaHasInappropriateColor: false,
    abnormalLochiaHasHeavyBleeding: false,
    abnormalLochiaHasFoulOdor: false,
    abnormalLochiaOther: '',
    hasAbnormalUterine: false,
    abnormalUterineFundalLocation: '',
    abnormalUterineFundalFeel: '',
    areContractionsPresent: false,
    contractionsStrength: '',
    contractionsDuration: '',
    contractionsFrequency: '',
    hasFetalHeartTones: false,
    fetalHeartTonesAccelerationsNoted: '',
    fetalHeartTonesDecelerationsNoted: '',
    fetalHeartTonesVariability: '',
    hasAbnormalVaginalAssessment: false,
    abnormalVaginalEpisiotomy: '',
    abnormalVaginalTear: '',
    abnormalVaginalDilation: '',
    abnormalVaginalStation: '',
    otherFindings: '',
  },
};


/**
 * Holds information about the patient's genitourinary assessment.
 */

export class GenitourinaryAssessment {
  urineColor: string;
  urineCharacteristics: string[];
  urinaryPattern: string[];
  // Pediatric only
  abnormalTestes: boolean;
  testesAbnormalityDetails: string;
  abnormalVaginalOpening: boolean;
  vaginalOpeningAbnormalityDetails: string;

  foleyJustification: string;
  foleySecurement: string;

  // OB only
  hasAbnormalLochia: boolean;
  abnormalLochiaHasInappropriateColor: boolean;
  abnormalLochiaHasHeavyBleeding: boolean;
  abnormalLochiaHasFoulOdor: boolean;
  abnormalLochiaOther: string;

  hasAbnormalUterine: boolean;
  abnormalUterineFundalLocation: string;
  abnormalUterineFundalFeel: string;
  areContractionsPresent: boolean;
  contractionsStrength: string;
  contractionsDuration: string;
  contractionsFrequency: string;
  hasFetalHeartTones: boolean;
  fetalHeartTonesAccelerationsNoted: string;
  fetalHeartTonesDecelerationsNoted: string;
  fetalHeartTonesVariability: string;
  hasAbnormalVaginalAssessment: boolean;
  abnormalVaginalEpisiotomy: string;
  abnormalVaginalTear: string;
  abnormalVaginalDilation: string;
  abnormalVaginalStation: string;
  otherFindings: string;

  constructor(data: Partial<GenitourinaryFields>) {
    const is = GenitourinaryAssessmentHelpers.initialState;

    this.urineColor = data.urineColor || is['urineColor'];
    this.urineCharacteristics = data.urineCharacteristics || is['urineCharacteristics'];
    this.urinaryPattern = data.urinaryPattern || is['urinaryPattern'];
    this.abnormalTestes = data.abnormalTestes ?? is['abnormalTestes'];
    this.testesAbnormalityDetails = data.testesAbnormalityDetails || is['testesAbnormalityDetails'];
    this.abnormalVaginalOpening = data.abnormalVaginalOpening ?? is['abnormalVaginalOpening'];
    this.vaginalOpeningAbnormalityDetails = data.vaginalOpeningAbnormalityDetails || is['vaginalOpeningAbnormalityDetails'];
    this.foleyJustification = data.foleyJustification || is['foleyJustification'];
    this.foleySecurement = data.foleySecurement || is['foleySecurement'];

    this.hasAbnormalLochia = data.hasAbnormalLochia || is['hasAbnormalLochia'];
    this.abnormalLochiaHasInappropriateColor = data.abnormalLochiaHasInappropriateColor || is['abnormalLochiaHasInappropriateColor'];
    this.abnormalLochiaHasHeavyBleeding = data.abnormalLochiaHasHeavyBleeding || is['abnormalLochiaHasHeavyBleeding'];
    this.abnormalLochiaHasFoulOdor = data.abnormalLochiaHasFoulOdor || is['abnormalLochiaHasFoulOdor'];
    this.abnormalLochiaOther = data.abnormalLochiaOther || is['abnormalLochiaOther'];
    this.hasAbnormalUterine = data.hasAbnormalUterine || is['hasAbnormalUterine'];
    this.abnormalUterineFundalFeel = data.abnormalUterineFundalFeel || is['abnormalUterineFundalFeel'];
    this.abnormalUterineFundalLocation = data.abnormalUterineFundalLocation || is['abnormalUterineFundalLocation'];
    this.areContractionsPresent = data.areContractionsPresent || is['areContractionsPresent'];
    this.contractionsStrength = data.contractionsStrength || is['contractionsStrength'];
    this.contractionsDuration = data.contractionsDuration || is['contractionsDuration'];
    this.contractionsFrequency = data.contractionsFrequency || is['contractionsFrequency'];
    this.hasFetalHeartTones = data.hasFetalHeartTones || is['hasFetalHeartTones'];
    this.fetalHeartTonesAccelerationsNoted = data.fetalHeartTonesAccelerationsNoted || is['fetalHeartTonesAccelerationsNoted'];
    this.fetalHeartTonesDecelerationsNoted = data.fetalHeartTonesDecelerationsNoted || is['fetalHeartTonesDecelerationsNoted'];
    this.fetalHeartTonesVariability = data.fetalHeartTonesVariability || is['fetalHeartTonesVariability'];
    this.hasAbnormalVaginalAssessment = data.hasAbnormalVaginalAssessment || is['hasAbnormalVaginalAssessment'];
    this.abnormalVaginalEpisiotomy = data.abnormalVaginalEpisiotomy || is['abnormalVaginalEpisiotomy'];
    this.abnormalVaginalTear = data.abnormalVaginalTear || is['abnormalVaginalTear'];
    this.abnormalVaginalDilation = data.abnormalVaginalDilation || is['abnormalVaginalDilation'];
    this.abnormalVaginalStation = data.abnormalVaginalStation || is['abnormalVaginalStation'];
    this.otherFindings = data.otherFindings || is['otherFindings'];
  }
}

function createGenitourinaryFormGroup(genitourinaryAssessment?: GenitourinaryAssessment) {
  const data = genitourinaryAssessment || new GenitourinaryAssessment(GenitourinaryAssessmentHelpers.initialState);
  return new FormGroup<ControlsOf<GenitourinaryFields>>({
    urineColor: new FormControl<string | null>(data.urineColor),
    urineCharacteristics: new FormControl<string[] | null>(data.urineCharacteristics),
    urinaryPattern: new FormControl<string[] | null>(data.urinaryPattern),
    abnormalTestes: new FormControl<boolean | null>(data.abnormalTestes),
    testesAbnormalityDetails: new FormControl<string | null>(data.testesAbnormalityDetails),
    abnormalVaginalOpening: new FormControl<boolean | null>(data.abnormalVaginalOpening),
    vaginalOpeningAbnormalityDetails: new FormControl<string | null>(data.vaginalOpeningAbnormalityDetails),
    foleyJustification: new FormControl<string | null>(data.foleyJustification),
    foleySecurement: new FormControl<string | null>(data.foleySecurement),
    hasAbnormalLochia: new FormControl<boolean | null>(data.hasAbnormalLochia),
    abnormalLochiaHasInappropriateColor: new FormControl<boolean | null>(data.abnormalLochiaHasInappropriateColor),
    abnormalLochiaHasHeavyBleeding: new FormControl<boolean | null>(data.abnormalLochiaHasHeavyBleeding),
    abnormalLochiaHasFoulOdor: new FormControl<boolean | null>(data.abnormalLochiaHasFoulOdor),
    abnormalLochiaOther: new FormControl<string | null>(data.abnormalLochiaOther),
    hasAbnormalUterine: new FormControl<boolean | null>(data.hasAbnormalUterine),
    abnormalUterineFundalLocation: new FormControl<string | null>(data.abnormalUterineFundalLocation),
    abnormalUterineFundalFeel: new FormControl<string | null>(data.abnormalUterineFundalFeel),
    areContractionsPresent: new FormControl<boolean | null>(data.areContractionsPresent),
    contractionsStrength: new FormControl<string | null>(data.contractionsStrength),
    contractionsDuration: new FormControl<string | null>(data.contractionsDuration),
    contractionsFrequency: new FormControl<string | null>(data.contractionsFrequency),
    hasFetalHeartTones: new FormControl<boolean | null>(data.hasFetalHeartTones),
    fetalHeartTonesAccelerationsNoted: new FormControl<string | null>(data.fetalHeartTonesAccelerationsNoted),
    fetalHeartTonesDecelerationsNoted: new FormControl<string | null>(data.fetalHeartTonesDecelerationsNoted),
    fetalHeartTonesVariability: new FormControl<string | null>(data.fetalHeartTonesVariability),
    hasAbnormalVaginalAssessment: new FormControl<boolean | null>(data.hasAbnormalVaginalAssessment),
    abnormalVaginalEpisiotomy: new FormControl<string | null>(data.abnormalVaginalEpisiotomy),
    abnormalVaginalTear: new FormControl<string | null>(data.abnormalVaginalTear),
    abnormalVaginalDilation: new FormControl<string | null>(data.abnormalVaginalDilation),
    abnormalVaginalStation: new FormControl<string | null>(data.abnormalVaginalStation),
    otherFindings: new FormControl<string | null>(data.otherFindings),
  });
}

function genitourinaryAssessmentToPDF(pdf: AssessmentPdfGenerator, assessment: GenitourinaryAssessment,
  patient: Patient
): void {
  pdf.addSectionTitle("Genitourinary");
  pdf.addTwoColumnText("Urine Characteristics", assessment.urineCharacteristics.join(", "),
    "Urine Color", assessment.urineColor);
  pdf.addOneColumnText("Urine Pattern", assessment.urinaryPattern.join(", "));

  if (patient.isPediatric()) {

    pdf.addOneColumnText("Abnormal Testes", assessment.abnormalTestes ? "Yes" : "No");
    if (assessment.abnormalTestes) {
      pdf.addOneColumnText("Abnormal Testes Details", assessment.testesAbnormalityDetails);
    }

    pdf.addOneColumnText("Abnormal Vaginal Opening", assessment.abnormalVaginalOpening ? "Yes" : "No");
    if (assessment.abnormalVaginalOpening) {
      pdf.addOneColumnText("Abnormal Vaginal Opening Details", assessment.vaginalOpeningAbnormalityDetails);
    }

  }

  pdf.currY += 10;
  pdf.pdf.text("Foley", pdf.margin, pdf.currY);
  pdf.currY += 20;
  pdf.addOneColumnText("Justification", assessment.foleyJustification);
  pdf.addOneColumnText("Securement", assessment.foleySecurement);

  if (patient.isObstetric()) {
    if (assessment.hasAbnormalLochia) {
      pdf.currY += 10;
      pdf.pdf.text("Abnormal Lochia", pdf.margin, pdf.currY);
      pdf.currY += 20;

      pdf.addTwoColumnText("Inappropriate Color", assessment.abnormalLochiaHasInappropriateColor ? "Yes" : "No",
        "Heavy Bleeding", assessment.abnormalLochiaHasHeavyBleeding ? "Yes" : "No");
      pdf.addTwoColumnText("Foul Odor", assessment.abnormalLochiaHasFoulOdor ? "Yes" : "No",
        "Other", assessment.abnormalLochiaOther);
    }

    if (assessment.hasAbnormalUterine) {
      pdf.currY += 10;
      pdf.pdf.text("Abnormal Uterine Assessment", pdf.margin, pdf.currY);
      pdf.currY += 20;

      pdf.addTwoColumnText("Fundal Location", assessment.abnormalUterineFundalLocation,
        "Fundal Feel", assessment.abnormalUterineFundalFeel);
      if (assessment.areContractionsPresent) {
        pdf.addTwoColumnText("Contraction Strength", assessment.contractionsStrength,
          "Contraction Duration", assessment.contractionsDuration);
        pdf.addOneColumnText("Contraction Frequency", assessment.contractionsFrequency);
      }

      if (assessment.hasFetalHeartTones) {
        pdf.addTwoColumnText("Accelerations Noted", assessment.fetalHeartTonesAccelerationsNoted,
          "Decelerations Noted", assessment.fetalHeartTonesDecelerationsNoted);
        pdf.addOneColumnText("FHT Variability", assessment.fetalHeartTonesVariability);
      }
    }

    if (assessment.hasAbnormalVaginalAssessment) {
      pdf.currY += 10;
      pdf.pdf.text("Abnormal Vaginal Assessment", pdf.margin, pdf.currY);
      pdf.currY += 20;

      pdf.addTwoColumnText("Episiotomy", assessment.abnormalVaginalEpisiotomy,
        "Tear", assessment.abnormalVaginalTear);
      pdf.addTwoColumnText("Dilation", assessment.abnormalVaginalDilation,
        "Station", assessment.abnormalVaginalStation);
    }
  }
  pdf.addOneColumnText("Other Findings", assessment.otherFindings);
}

function formToGenitourinaryAssessment(fg: GenitourinaryFormGroup) {
  return new GenitourinaryAssessment({
    urineColor: fg.controls.urineColor.value!,
    urineCharacteristics: fg.controls.urineCharacteristics.value!,
    urinaryPattern: fg.controls.urinaryPattern.value!,
    abnormalTestes: fg.controls.abnormalTestes.value!,
    testesAbnormalityDetails: fg.controls.testesAbnormalityDetails.value!,
    abnormalVaginalOpening: fg.controls.abnormalVaginalOpening.value!,
    vaginalOpeningAbnormalityDetails: fg.controls.vaginalOpeningAbnormalityDetails.value!,
    foleyJustification: fg.controls.foleyJustification.value!,
    foleySecurement: fg.controls.foleySecurement.value!,

    hasAbnormalLochia: fg.controls.hasAbnormalLochia.value!,
    abnormalLochiaHasInappropriateColor: fg.controls.abnormalLochiaHasInappropriateColor.value!,
    abnormalLochiaHasHeavyBleeding: fg.controls.abnormalLochiaHasHeavyBleeding.value!,
    abnormalLochiaHasFoulOdor: fg.controls.abnormalLochiaHasFoulOdor.value!,
    abnormalLochiaOther: fg.controls.abnormalLochiaOther.value!,
    hasAbnormalUterine: fg.controls.hasAbnormalUterine.value!,
    abnormalUterineFundalLocation: fg.controls.abnormalUterineFundalLocation.value!,
    abnormalUterineFundalFeel: fg.controls.abnormalUterineFundalFeel.value!,
    areContractionsPresent: fg.controls.areContractionsPresent.value!,
    contractionsStrength: fg.controls.contractionsStrength.value!,
    contractionsDuration: fg.controls.contractionsDuration.value!,
    contractionsFrequency: fg.controls.contractionsFrequency.value!,
    hasFetalHeartTones: fg.controls.hasFetalHeartTones.value!,
    fetalHeartTonesAccelerationsNoted: fg.controls.fetalHeartTonesAccelerationsNoted.value!,
    fetalHeartTonesDecelerationsNoted: fg.controls.fetalHeartTonesDecelerationsNoted.value!,
    fetalHeartTonesVariability: fg.controls.fetalHeartTonesVariability.value!,
    hasAbnormalVaginalAssessment: fg.controls.hasAbnormalVaginalAssessment.value!,
    abnormalVaginalEpisiotomy: fg.controls.abnormalVaginalEpisiotomy.value!,
    abnormalVaginalTear: fg.controls.abnormalVaginalTear.value!,
    abnormalVaginalDilation: fg.controls.abnormalVaginalDilation.value!,
    abnormalVaginalStation: fg.controls.abnormalVaginalStation.value!,
    otherFindings: fg.controls.otherFindings.value!,
  });
}


